/**
 * Marketplace catalog API client (anonymous, ETag-aware).
 *
 * Powers the PUBLIC marketplace surface (Phase 1):
 *   - GET /api/cluster/catalogs/public            → marketplace cards (list)
 *   - GET /api/cluster/catalogs/public/{id}       → full app detail + docs
 *
 * Both endpoints are anonymous and read-only (no Authorization required), but
 * we mirror `cluster-api.js`: best-effort token attach (harmless if present),
 * the unified `{success,message,data}` envelope unwrap, and the same
 * env-config base-URL resolution. See `cluster-api/docs/CATALOGS_PUBLIC_API.md`.
 *
 * Caching: the endpoints emit `ETag` + `Cache-Control: public, max-age=60`.
 * For cross-origin GETs the browser may not surface conditional revalidation,
 * so we keep a small in-memory ETag store and last-good payload cache, send
 * `If-None-Match`, and on a `304 Not Modified` return the cached copy. We
 * widen `validateStatus` so a 304 does not reject.
 */
import axios from 'axios'
import { attachOrgLockedHandler } from '@/services/org-locked'
import store from '@/store'
import { envConfig } from '@/utils/env-config'
import { readToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'

const BASE_URL = envConfig.VUE_APP_API_BASE_URL || envConfig.VITE_API_BASE_URL
let API_VERSION = envConfig.VUE_APP_API_VERSION || envConfig.VITE_API_VERSION || ''
API_VERSION = API_VERSION.replace(/['"]/g, '').trim()
if (API_VERSION === 'v1' || API_VERSION === '/v1' || API_VERSION === 'api/v1') {
  API_VERSION = ''
}

const marketplaceAxios = axios.create({
  baseURL: API_VERSION ? `${BASE_URL}/api/${API_VERSION}` : `${BASE_URL}/api`,
  timeout: envConfig.VUE_APP_API_TIMEOUT || envConfig.VITE_API_TIMEOUT || 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  // A 304 must NOT reject — we resolve it and serve the cached payload.
  validateStatus: (s) => s === 304 || (s >= 200 && s < 300)
})

// Best-effort token attach. The catalog endpoints are public, but if the
// visitor happens to be logged in we send the token (harmless), exactly like
// `cluster-api.js` does for `/plans`.
marketplaceAxios.interceptors.request.use((config) => {
  const token = store.state.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Typed error so the detail view can distinguish a missing app (404) from a
 * generic network/server failure and show the dedicated "not found" panel.
 */
export class NotFoundError extends Error {
  constructor(message, resource) {
    super(message || 'Not found')
    this.name = 'NotFoundError'
    this.resource = resource
    this.notFound = true
  }
}

// Module-level caches. Keyed by 'list' for the catalog list and by template id
// for detail. These live for the lifetime of the SPA session.
const etagStore = new Map() // key -> etag string
const payloadStore = new Map() // key -> last-good unwrapped payload

function unwrap(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

function captureEtag(key, response) {
  // axios lower-cases response header keys.
  const etag = response?.headers?.etag || response?.headers?.ETag
  if (etag) {
    etagStore.set(key, etag)
  }
}

export const marketplaceApi = {
  /**
   * List marketplace cards. Returns an array of card objects.
   * Sends If-None-Match from the in-memory store; a 304 returns the cached
   * array. Captures the response ETag for next time.
   */
  async listCatalog() {
    const key = 'list'
    const headers = {}
    if (etagStore.has(key)) {
      headers['If-None-Match'] = etagStore.get(key)
    }

    let response
    try {
      response = await marketplaceAxios.get('/cluster/catalogs/public', { headers })
    } catch (error) {
      // If the conditional request fails but we have a cached copy, degrade to
      // it rather than blanking the grid on a transient hiccup.
      if (payloadStore.has(key)) {
        return payloadStore.get(key)
      }
      throw error
    }

    if (response.status === 304 && payloadStore.has(key)) {
      return payloadStore.get(key)
    }

    captureEtag(key, response)
    const data = unwrap(response) || []
    payloadStore.set(key, data)
    return data
  },

  /**
   * Fetch one app's full detail (card fields + documentation_markdown +
   * structured_documentation + specification). ETag handled per id.
   * A 404 throws a NotFoundError so the view renders the not-found panel.
   */
  async getCatalog(id) {
    const key = `detail:${id}`
    const headers = {}
    if (etagStore.has(key)) {
      headers['If-None-Match'] = etagStore.get(key)
    }

    let response
    try {
      response = await marketplaceAxios.get(
        `/cluster/catalogs/public/${encodeURIComponent(id)}`,
        { headers }
      )
    } catch (error) {
      const status = error?.response?.status
      if (status === 404) {
        const body = error?.response?.data || {}
        throw new NotFoundError(
          body.message || `Marketplace app '${id}' not found`,
          body.resource || id
        )
      }
      if (payloadStore.has(key)) {
        return payloadStore.get(key)
      }
      throw error
    }

    if (response.status === 304 && payloadStore.has(key)) {
      return payloadStore.get(key)
    }

    captureEtag(key, response)
    const data = unwrap(response)
    payloadStore.set(key, data)
    return data
  }
}

export default marketplaceAxios

// 423 organization_locked → the locked-organisation page, once per burst.
attachOrgLockedHandler(marketplaceAxios)
